import { iniciarFoco } from "./foco";
import { iniciarMenu } from "./menu";
import { iniciarAnclas, iniciarNavegacion } from "./nav";
import { iniciarTema } from "./tema";

/**
 * Arranque del cliente.
 *
 * La página ya llega pintada y completa (HTML prerenderizado): este módulo solo añade
 * comportamiento. Lo imprescindible —tema, menú, navegación, foco de luz y año del pie—
 * pesa unos pocos KB y va aquí. Las animaciones (Motion) se cargan en un trozo aparte
 * cuando el navegador queda libre: nada de lo que animan hace falta para leer la página, y
 * así no compiten con el primer pintado ni bloquean el hilo principal mientras carga.
 */

// El año del pie se escribe al construir; aquí se pone al día por si la web lleva tiempo
// sin desplegarse (en enero mostraría el año anterior).
const anio = String(new Date().getFullYear());
document.querySelectorAll<HTMLElement>("[data-anio-actual]").forEach((el) => {
  if (el.textContent !== anio) el.textContent = anio;
});

iniciarTema();
iniciarMenu();
iniciarNavegacion();
iniciarAnclas();
iniciarFoco();

// Movimiento reducido (segunda capa del estándar §1.6): ni se descarga el módulo. La página
// se queda en su estado final, que es el que trae el HTML.
const reducido = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
if (!reducido) {
  trasElPrimerPintado(() => {
    // La preparación va en su propia tarea y justo después de un fotograma, no a continuación
    // de evaluar el módulo: juntas, y sumadas a la maquetación que hubiera pendiente (sus
    // medidas la fuerzan), superaban los 50 ms en un móvil lento y bloqueaban el hilo
    // principal justo cuando la persona empieza a tocar la página.
    void import("./animaciones").then((m) =>
      window.requestAnimationFrame(() => window.setTimeout(() => m.iniciarAnimaciones(), 0)),
    );
  });
}

/**
 * Pide las animaciones cuando la página ya está en pantalla. Con un simple
 * `requestIdleCallback`, y también con «tras el `load`, dos fotogramas», el trozo de Motion se
 * pedía unos milisegundos ANTES de que constara el primer pintado (el navegador pinta el
 * fotograma, pero tarda un poco en presentarlo). Lighthouse cuenta dentro del LCP todo lo que
 * se pidió antes, y esa descarga engordaba el LCP simulado en móvil. La entrada
 * `first-contentful-paint` solo llega con el pintado ya presentado.
 */
function trasElPrimerPintado(fn: () => void): void {
  const tipos = window.PerformanceObserver?.supportedEntryTypes ?? [];
  if (tipos.includes("paint")) {
    const observador = new PerformanceObserver((lista) => {
      if (!lista.getEntriesByName("first-contentful-paint").length) return;
      observador.disconnect();
      cuandoLibre(fn);
    });
    observador.observe({ type: "paint", buffered: true });
    return;
  }
  // Sin Paint Timing: tras la carga y dos fotogramas, que garantizan al menos uno pintado.
  const tras = () => window.requestAnimationFrame(() => window.requestAnimationFrame(() => cuandoLibre(fn)));
  if (document.readyState === "complete") tras();
  else window.addEventListener("load", tras, { once: true });
}

function cuandoLibre(fn: () => void): void {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opciones?: { timeout: number }) => number;
  };
  if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 1500 });
  else window.setTimeout(fn, 250);
}
